import { useEffect, useState, type JSX } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector, useStore } from "react-redux";
import * as Sentry from "@sentry/react";
import type { AppDispatch, RootState } from "@/store/store";
import userService from "@/endpoints/userService";
import { setPendingSelectionIfChanged } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import {
  buildValidateRequest,
  type SelectionsRefOrigin,
  type TruncatedKinds,
} from "@/utils/selections/buildValidateRequest";
import {
  partitionSelections,
  hasAdoptable,
  hasBlocked,
  type SelectionPartition,
} from "@/utils/selections/partitionSelections";
import { resolveOwnedBrandRefs } from "@/utils/selections/resolveOwnedBrandRefs";
import { resolveOwnedSegmentCodes } from "@/utils/selections/resolveOwnedSegmentCodes";
import { MAX_APPLIED_SEGMENTS } from "@/components/projectCommon/Filters/reducer/appliedSegmentsReducer";
import { selectAppliedSegmentCodes } from "@/store/slices/segments/selectors";
import { setSegmentApplied } from "@/store/slices/segments/segments.thunk";
import type { CONFIG_SCHEMA } from "@/store/slices/screenConfig/screenConfigSlice";
import SelectionAdoptionModal from "./SelectionAdoptionModal";
import { useAdoptSelections } from "./useAdoptSelections";

// Fire the read-path validate at most once per session; a module-level guard
// (not a ref) so it survives route remounts.
let hasRunThisSession = false;

const isEnabled = (): boolean =>
  import.meta.env.VITE_FLAG_SELECTIONS_VALIDATION === "true";

/**
 * Read-path orchestrator (Phase 1) + adoption modal wiring (Phase 2). On the first app
 * load carrying a shared selection, validates the URL's references against the caller's
 * access, silently re-selects the brand sets the caller already owns (mapping a shared
 * source ULID to the caller's own copy), and opens the adoption modal when the response
 * carries ELIGIBLE (adoptable) or NOT_ELIGIBLE (blocked) items to surface.
 * Gated behind VITE_FLAG_SELECTIONS_VALIDATION (default off) so it is inert until rollout.
 */
const ValidateSelections = (): JSX.Element | null => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const location = useLocation();
  const isDataFetched = useSelector(
    (s: RootState) => s.activeGroup.bootstrapDataStatus.isDataFetched,
  );
  const filtersReady = useSelector(
    (s: RootState) => s.pendingSelection.filtersReady,
  );
  const segmentsReady = useSelector(
    (s: RootState) => s.pendingSelection.segmentsReady,
  );
  // Subscribed (not a one-off store.getState() read) because these feed the modal's
  // render-time cap/headroom: screenConfig can populate after this component's first
  // render (selectionListener sets it on selection commit), and applied segments can
  // change while the modal is open — both need to force a re-render to stay correct.
  const segmentFilterModalMode = useSelector(
    (s: RootState) => (s.screenConfig as CONFIG_SCHEMA | undefined)?.segmentFilterModalMode,
  );
  const appliedSegmentCodes = useSelector(selectAppliedSegmentCodes);
  const [modalState, setModalState] = useState<{
    partition: SelectionPartition;
    origin: SelectionsRefOrigin;
    truncated: TruncatedKinds;
  } | null>(null);
  const { adopt, isApplying, errorMessage } = useAdoptSelections();

  useEffect(() => {
    // Wait for the bootstrap selection to commit (it blocks on filters+segments readiness)
    // before reading the current selection, so we union against committed state, not empty.
    if (
      !isEnabled() ||
      hasRunThisSession ||
      !isDataFetched ||
      !filtersReady ||
      !segmentsReady
    )
      return;

    const { request, origin, truncated } = buildValidateRequest(
      new URLSearchParams(location.search),
    );
    if (
      !request.brands &&
      !request.brandSets &&
      !request.filters &&
      !request.segments
    ) {
      return;
    }

    hasRunThisSession = true;

    void (async () => {
      try {
        const response = await userService.selections.validate(request);
        // Single partition call, reused below for both the owned-brand-set
        // auto-selection (Phase 1) and the adoption modal (Phase 2). The modal
        // check must run before the owned-only early return below, since a
        // response can carry adoptable/blocked items with no OWNED/ALREADY_HAVE
        // brand sets at all (e.g. a purely ELIGIBLE shared set).
        const partition = partitionSelections(response);

        if (hasAdoptable(partition) || hasBlocked(partition)) {
          setModalState({ partition, origin, truncated });
        }

        const owned = resolveOwnedBrandRefs(partition.usable.brandSets, origin);
        if (owned.brands.length || owned.secondaryBrands.length) {
          const current = store.getState().userSelection;
          dispatch(
            setPendingSelectionIfChanged({
              brands: Array.from(
                new Set([...(current.brands ?? []), ...owned.brands]),
              ),
              secondaryBrands: Array.from(
                new Set([...(current.secondaryBrands ?? []), ...owned.secondaryBrands]),
              ),
            }),
          );
        }

        // Segment remap must run regardless of whether any brand set resolved above —
        // a link can carry only foreign brand sets alongside an ALREADY_HAVE segment.
        const ownedSegmentCodes = resolveOwnedSegmentCodes(partition.usable.segments);
        if (ownedSegmentCodes.length) {
          // Read fresh here rather than closing over the subscribed `segmentFilterModalMode`
          // above: the validate request just awaited a network round trip, during which
          // screenConfig can have populated (or applied segments changed), so the
          // dispatch decision needs the state as of right now, not as of first render.
          // screenConfig's slice-level type is `CONFIG_SCHEMA | {}` (empty before any
          // route registers its config), so RootState narrows the field access away —
          // cast through CONFIG_SCHEMA as the rest of the codebase does (useAdoptSelections.ts).
          const modeAtDispatch = (store.getState().screenConfig as CONFIG_SCHEMA | undefined)
            ?.segmentFilterModalMode;
          if (modeAtDispatch === "SEGMENT_ONLY") {
            const appliedAtDispatch = selectAppliedSegmentCodes(store.getState());
            const headroom = Math.max(0, MAX_APPLIED_SEGMENTS - appliedAtDispatch.length);
            ownedSegmentCodes
              .filter((code) => !appliedAtDispatch.includes(code))
              .slice(0, headroom)
              .forEach((code) => dispatch(setSegmentApplied({ code, isApplied: true })));
          } else {
            const current = store.getState().userSelection.segments ?? [];
            dispatch(
              setPendingSelectionIfChanged({
                segments: Array.from(new Set([...current, ...ownedSegmentCodes])),
              }),
            );
          }
        }
      } catch (err) {
        // Read path is best-effort: capture, but never block the app.
        Sentry.captureException(err, {
          tags: { feature: "selections", op: "validate" },
        });
      }
    })();
  }, [isDataFetched, filtersReady, segmentsReady, location.search, dispatch, store]);

  if (!modalState) return null;

  const segmentHeadroom =
    segmentFilterModalMode === "SEGMENT_ONLY"
      ? Math.max(0, MAX_APPLIED_SEGMENTS - appliedSegmentCodes.length)
      : undefined;

  return (
    <SelectionAdoptionModal
      open
      partition={modalState.partition}
      truncated={modalState.truncated}
      segmentHeadroom={segmentHeadroom}
      isApplying={isApplying}
      errorMessage={errorMessage}
      onClose={() => setModalState(null)}
      onAdd={async (brandSetUlids, segmentCodes) => {
        const ok = await adopt(brandSetUlids, segmentCodes, modalState.origin);
        if (ok) setModalState(null);
      }}
    />
  );
};

export default ValidateSelections;
